import {RoomState} from '@lib/infrastructure';
import {
  getFirstAvailableTimeslot,
  getFirstBookingWhithinInterval,
  getFirstBookingAfterAboutToEndPeriod,
  getLastAboutToEndBooking,
  getMeetingRoomSuggestionDataById,
  useAppSelector,
  getAppLanguage,
  formatDuration,
} from '@lib/store';
import {differenceInMinutes, endOfDay, intervalToDuration, parseISO} from 'date-fns';
import {useTranslation} from 'react-i18next';
import {IndicatorStatus} from './types';
import {formatDate, stripSecondsAndMilliseconds} from '@lib/utils';
import {useCallback} from 'react';

export type ScheduleStatus = {
  scheduleStatus: IndicatorStatus;
  scheduleTitle: string;
  scheduleSubtitle?: string;
};

export const useRoomScheduleStatus = (
  buildingId: string,
  roomId: string,
  startDateTime: string,
  endDateTime: string,
  roomEmail: string | undefined,
): ScheduleStatus | undefined => {
  const appLanguage = useAppSelector(getAppLanguage);
  const {t} = useTranslation();
  const {state: roomState, markedAsBookedLocally} =
    useAppSelector((state) => getMeetingRoomSuggestionDataById(state, buildingId, roomId)) ?? {};

  const getFormattedDateAsTime = useCallback(
    (date: Date | undefined) => (date ? formatDate(date, 'HH:mm', appLanguage) : ''),
    [appLanguage],
  );

  const parsedStart = parseISO(startDateTime);
  const parsedEnd = parseISO(endDateTime);
  const meetingDuration = intervalToDuration({start: parsedStart, end: parsedEnd});

  // next booking at {time}
  const nextBooking = useAppSelector((rootState) =>
    getFirstBookingWhithinInterval(rootState, buildingId, roomId, {minStartDateString: startDateTime}),
  );
  const formattedNextBookingStart = getFormattedDateAsTime(nextBooking?.start);
  const nextBookingAtText = nextBooking && t('hereAndNow:NextBookingStartsAt', {time: formattedNextBookingStart});
  // next booking today at {time}
  const nextBookingToday = useAppSelector((rootState) =>
    getFirstBookingWhithinInterval(rootState, buildingId, roomId, {
      minStartDateString: startDateTime,
      maxStartDateString: endOfDay(parsedStart).toISOString(),
    }),
  );
  const formattedNextBookingTodayStart = getFormattedDateAsTime(nextBookingToday?.start);
  const nextBookingTodayAtText = nextBookingToday
    ? t('hereAndNow:NextBookingStartsAt', {time: formattedNextBookingTodayStart})
    : t('hereAndNow:NoMoreBookingsForToday');
  // booked until {time}
  const {start: startOfNextFreeTimeslot} =
    useAppSelector((rootState) =>
      getFirstAvailableTimeslot(rootState, buildingId, roomId, startDateTime, meetingDuration),
    ) ?? {};
  const formattedStartOfNextFreeTimeslot = getFormattedDateAsTime(startOfNextFreeTimeslot);
  const bookedUntilText =
    startOfNextFreeTimeslot && t('hereAndNow:BookedUntil', {time: formattedStartOfNextFreeTimeslot});
  // Available for {duration}
  const duration = nextBooking?.start
    ? intervalToDuration({
        start: stripSecondsAndMilliseconds(parsedStart),
        end: nextBooking?.start,
      })
    : undefined;
  const formattedDuration = duration ? formatDuration(t, duration, true) : '';
  const availableForText =
    formattedDuration && t('hereAndNow:RoomAvailableForTitle', {countWithMinutesSuffix: formattedDuration});
  // available in {duration}
  const lastAboutToEndBooking = useAppSelector((rootState) =>
    getLastAboutToEndBooking(rootState, buildingId, roomId, startDateTime),
  );
  const endingMeetingOffset =
    lastAboutToEndBooking && differenceInMinutes(lastAboutToEndBooking.end, parsedStart, {roundingMethod: 'floor'});
  const availableInText = t('hereAndNow:RoomWillBeAvailableSoonTitle', {count: endingMeetingOffset});
  // next meeting after about to end period at {time}
  const firstMeetingAfterAboutToEnd = useAppSelector((rootState) =>
    getFirstBookingAfterAboutToEndPeriod(rootState, buildingId, roomId, startDateTime),
  );
  const firstMeetingAfterAboutToEndFormattedTime = getFormattedDateAsTime(firstMeetingAfterAboutToEnd?.start);
  const firstMeetingAfterAboutToEndAtText = firstMeetingAfterAboutToEnd
    ? t('hereAndNow:NextBookingStartsAt', {time: firstMeetingAfterAboutToEndFormattedTime})
    : t('hereAndNow:NoMoreBookingsForToday');

  if (markedAsBookedLocally) {
    return {
      scheduleStatus: 'error',
      scheduleTitle: t('Booked'),
    };
  }

  switch (roomState) {
    case RoomState.NotBookedAndUnknownSensor:
    case RoomState.NotBookedAndFree:
    case RoomState.NotBookedAndInUse:
    case RoomState.NotBooked:
      return {
        scheduleStatus: 'success',
        scheduleTitle: t('NotBooked'),
        scheduleSubtitle: nextBookingTodayAtText,
      };
    case RoomState.BookedAndUnknownSensor:
    case RoomState.BookedAndInUse:
    case RoomState.BookedAndFree:
    case RoomState.Booked:
      return {
        scheduleStatus: 'error',
        scheduleTitle: t('Booked'),
        scheduleSubtitle: bookedUntilText,
      };
    case RoomState.BookingAboutToStartAndUnknownSensor:
    case RoomState.BookingAboutToStartAndInUse:
    case RoomState.BookingAboutToStartAndFree:
    case RoomState.BookingAboutToStart:
      return {
        scheduleStatus: 'warning',
        scheduleTitle: availableForText,
        scheduleSubtitle: nextBookingAtText,
      };
    case RoomState.BookingAboutToEndAndUnknownSensor:
    case RoomState.BookingAboutToEndAndInUse:
    case RoomState.BookingAboutToEndAndFree:
    case RoomState.BookingAboutToEnd:
      return {
        scheduleStatus: 'warning',
        scheduleTitle: availableInText,
        scheduleSubtitle: firstMeetingAfterAboutToEndAtText,
      };
    case RoomState.Free:
    case RoomState.InUse:
    case RoomState.UnknownSensor:
    case RoomState.Untraceable:
      if (!roomEmail?.length) return;

      return {
        scheduleStatus: 'unknown',
        scheduleTitle: t('hereAndNow:Bookable'),
      };
    default:
      return;
  }
};
